import { HttpInterceptorFn, HttpErrorResponse, HttpEvent } from '@angular/common/http';
import { inject } from '@angular/core';
import { throwError, timer } from 'rxjs';
import { catchError, retry, tap } from 'rxjs/operators';
import { ErrorHandlerService } from '../services/error-handler.service';
import { LoggerService } from '../services/logger.service';
import { NetworkErrorHandlerService } from '../services/network-error-handler.service';
import { APP_CONFIG } from '../../config/app.config';

const RETRY_CONFIG = {
  BASE_DELAY_MS: 1000,
  MAX_DELAY_MS: 10000,
  MAX_RETRY_ATTEMPTS: 3
} as const;

const RETRYABLE_STATUS_CODES = [0, 408, 429, 500, 502, 503, 504];

export const errorInterceptor: HttpInterceptorFn = (request, next) => {
  const errorHandler = inject(ErrorHandlerService);
  const logger = inject(LoggerService);
  const networkErrorHandler = inject(NetworkErrorHandlerService);
  const config = APP_CONFIG;

  const startTime = Date.now();

  logger.debug(
    `HTTP Request: ${request.method} ${request.url}`,
    {
      method: request.method,
      url: request.url,
      headers: request.headers.keys(),
      body: request.body
    },
    'HTTP_INTERCEPTOR'
  );

  return next(request).pipe(
    tap((event: HttpEvent<unknown>) => {
      if (event.type === 4) {
        const response = event;
        const duration = Date.now() - startTime;

        logger.logApiCall(request.method, request.url, response.status, duration);
      }
    }),

    retry({
      count: config.api.retries,
      delay: (error: HttpErrorResponse, retryCount: number) => {
        if (shouldRetry(error, retryCount, config)) {
          const delay = Math.min(
            RETRY_CONFIG.BASE_DELAY_MS * Math.pow(2, retryCount),
            RETRY_CONFIG.MAX_DELAY_MS
          );
          logger.info(
            `Retrying request: ${request.method} ${request.url} (attempt ${retryCount + 1})`,
            {
              attempt: retryCount + 1,
              delay,
              error: error.status
            },
            'HTTP_INTERCEPTOR'
          );

          return timer(delay);
        }

        return throwError(() => error);
      },
      resetOnSuccess: true
    }),

    catchError((error: HttpErrorResponse) => {
      const duration = Date.now() - startTime;

      // Check if this error should be ignored
      if (networkErrorHandler.shouldIgnoreError(error)) {
        logger.debug(
          `Ignoring network error: ${request.method} ${request.url}`,
          {
            error: error.status,
            duration,
            request: {
              method: request.method,
              url: request.url
            }
          },
          'HTTP_INTERCEPTOR'
        );
        return throwError(() => error);
      }

      logger.error(
        `HTTP Error: ${request.method} ${request.url}`,
        {
          error,
          duration,
          request: {
            method: request.method,
            url: request.url,
            headers: request.headers.keys()
          }
        },
        'HTTP_INTERCEPTOR'
      );

      errorHandler.handleError(error, 'HTTP_INTERCEPTOR');

      return throwError(() => error);
    })
  );
};

function shouldRetry(
  error: HttpErrorResponse,
  retryCount: number,
  config: typeof APP_CONFIG
): boolean {
  if (retryCount >= config.api.retries) {
    return false;
  }

  return RETRYABLE_STATUS_CODES.includes(error.status);
}
